/**
 * Seção de gerenciamento de trechos quilométricos da OS.
 * Cada trecho define um intervalo (km_inicial → km_final) e o sistema
 * classifica automaticamente os apontamentos (pintura + placas) pelo km.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, TrashSimple, MapPinLine } from "@phosphor-icons/react";

function normalizeKm(v) {
  return String(v || "").replace(/[^\d+]/g, "");
}

function kmToM(v) {
  if (!v) return 0;
  const s = String(v).trim();
  if (s.includes("+")) {
    const [km, m] = s.split("+");
    return (parseInt(km || "0", 10) || 0) * 1000 + (parseInt(m || "0", 10) || 0);
  }
  return parseFloat(s) || 0;
}

const EMPTY = { nome: "", km_inicial: "", km_final: "", ordem: 0 };

export default function TrechosSection({ osId }) {
  const [trechos, setTrechos] = useState([]);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get(`/ordens/${osId}/trechos`).then((r) => setTrechos(r.data));
  };
  useEffect(load, [osId]);

  const openNew = () => { setForm({ ...EMPTY, ordem: trechos.length + 1 }); setEditId(null); setModal(true); };
  const openEdit = (t) => { setForm({ nome: t.nome, km_inicial: t.km_inicial, km_final: t.km_final, ordem: t.ordem || 0 }); setEditId(t.id); setModal(true); };

  const save = async () => {
    if (!form.nome || !form.km_inicial || !form.km_final) {
      toast.error("Preencha nome, km inicial e km final");
      return;
    }
    if (kmToM(form.km_final) <= kmToM(form.km_inicial)) {
      toast.error("km final deve ser maior que km inicial");
      return;
    }
    setLoading(true);
    try {
      const payload = { os_id: osId, ...form, ordem: parseInt(form.ordem) || 0 };
      if (editId) {
        await api.put(`/trechos/${editId}`, payload);
        toast.success("Trecho atualizado — apontamentos re-classificados");
      } else {
        await api.post("/trechos", payload);
        toast.success("Trecho cadastrado");
      }
      setModal(false); load();
    } catch (e) {
      toast.error("Erro ao salvar trecho");
    } finally { setLoading(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Excluir o trecho "${t.nome}"? Apontamentos deste trecho ficarão sem classificação.`)) return;
    await api.delete(`/trechos/${t.id}`);
    toast.success("Trecho excluído");
    load();
  };

  return (
    <div className="pcp-card" style={{ padding: 20, marginBottom: 24 }} data-testid="trechos-section">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 className="font-display font-bold text-lg" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MapPinLine size={20} weight="bold" /> Trechos da Rodovia
        </h2>
        <button className="btn btn-primary btn-sm" onClick={openNew} data-testid="btn-add-trecho">
          <Plus size={14} weight="bold" /> Adicionar trecho
        </button>
      </div>
      <div style={{ fontSize: "0.8rem", color: "#71717A", marginBottom: 12 }}>
        Cadastre os trechos para o sistema classificar automaticamente cada apontamento no BDM.
      </div>

      {trechos.length === 0 ? (
        <div style={{ padding: 20, color: "#71717A", textAlign: "center", fontSize: "0.85rem", background: "#FAFAFA", borderRadius: 4 }}>
          Nenhum trecho cadastrado. Sem trechos, o BDM lista tudo em um único bloco "sem trecho".
        </div>
      ) : (
        <table className="pcp-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Nome</th>
              <th>Km inicial</th>
              <th>Km final</th>
              <th style={{ textAlign: "right" }}>Extensão</th>
              <th style={{ width: 100 }}></th>
            </tr>
          </thead>
          <tbody>
            {trechos.map((t) => (
              <tr key={t.id} data-testid={`trecho-row-${t.id}`}>
                <td style={{ color: "#71717A" }}>{t.ordem || "—"}</td>
                <td style={{ fontWeight: 600 }}>{t.nome}</td>
                <td className="font-mono">{t.km_inicial}</td>
                <td className="font-mono">{t.km_final}</td>
                <td className="font-mono" style={{ textAlign: "right" }}>
                  {((t.extensao_m || 0) / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} km
                </td>
                <td style={{ textAlign: "right" }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(t)} data-testid={`btn-edit-trecho-${t.id}`}><Pencil size={14} weight="bold" /></button>
                  <button className="btn btn-ghost btn-sm" onClick={() => remove(t)} data-testid={`btn-del-trecho-${t.id}`}><TrashSimple size={14} weight="bold" color="#DC2626" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal && (
        <div className="pcp-modal-backdrop" onClick={() => setModal(false)}>
          <div className="pcp-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <h3 className="font-display font-bold text-lg" style={{ marginBottom: 16 }}>
              {editId ? "Editar trecho" : "Novo trecho"}
            </h3>
            <div style={{ display: "grid", gap: 12 }}>
              <div>
                <label className="label">Nome do trecho</label>
                <input className="input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex.: Lote 1 - Zona rural" data-testid="input-trecho-nome" />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label className="label">Km inicial</label>
                  <input className="input" value={form.km_inicial} onChange={(e) => setForm({ ...form, km_inicial: normalizeKm(e.target.value) })} placeholder="0+000" data-testid="input-trecho-ki" />
                </div>
                <div>
                  <label className="label">Km final</label>
                  <input className="input" value={form.km_final} onChange={(e) => setForm({ ...form, km_final: normalizeKm(e.target.value) })} placeholder="5+000" data-testid="input-trecho-kf" />
                </div>
              </div>
              <div>
                <label className="label">Ordem (para o BDM)</label>
                <input type="number" className="input" value={form.ordem} onChange={(e) => setForm({ ...form, ordem: e.target.value })} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
              <button className="btn btn-outline" onClick={() => setModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={loading} data-testid="btn-save-trecho">
                {loading ? "Salvando…" : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
